import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useMutation } from "@apollo/client/react";
import { gql } from "graphql-tag";
import { toast } from "sonner";

export const TOGGLE_FACT_DESELECTED_MUTATION = gql`
  mutation ToggleFactDeselected($factId: UUID!, $deselected: Boolean!) {
    messageToggleFactDeselected(factId: $factId, deselected: $deselected)
  }
`;

/** The minimal shape this hook needs from each agent-contributed fact. Satisfied
 *  by the generated `Message.messageFacts` row in both Neptune and
 *  Venus. */
export interface DeselectableFact {
  id: string;
  /** Server deselection timestamp. Non-null = the fact was deselected (in this
   *  or a prior session) and is excluded from the next generation run. */
  deselectedAt?: string | null;
  /** True when a service agent deselected the fact; false for a supporting
   *  bullet the AI stored deselected (#706). */
  deselectedByUser?: boolean;
}

interface UseFactDeselectionOptions {
  messageId: string | null | undefined;
  /** The message's agent-contributed facts as returned by the server (raw, not
   *  the optimistic-merged notebook list). The rendered deselected set is seeded
   *  from each fact's `deselectedAt` so a prior session's deselections show on
   *  load. */
  facts: readonly DeselectableFact[] | null | undefined;
  /** Fired after a toggle is applied locally and the mutation is sent. Every
   *  toggle changes the injected fact set (AI bullets included), so the caller
   *  can unconditionally light its regeneration spinner. */
  onToggled?: (factId: string, deselected: boolean) => void;
}

interface UseFactDeselectionReturn {
  /** IDs of facts rendered as deselected (faded): server `deselectedAt` rows
   *  plus in-session overrides. */
  dismissedFactIds: Set<string>;
  /** The subset of `dismissedFactIds` the AI offered deselected and no
   *  service agent has touched: rendered as a suggestion, not a rejection. Any
   *  in-session toggle makes the state the agent's own. */
  suggestedFactIds: Set<string>;
  /** Toggle one fact's selection. Persists via messageToggleFactDeselected
   *  and triggers the same server-owned regeneration as add/edit/remove. */
  toggle: (factId: string) => void;
  /** Drop in-session overrides (the server state then governs rendering). */
  reset: () => void;
}

/**
 * Persists agent-contributed-fact deselection via the messageToggleFactDeselected
 * mutation and reflects it optimistically. Replaces the former session-local
 * useDismissedSet: deselecting now excludes the fact from the proposed-answer
 * pipeline (server-side) instead of only fading the row. Shared by Neptune's
 * ProposedAnswerTab and Venus's AgentPage, including their per-to-do cards.
 */
export function useFactDeselection({
  messageId,
  facts,
  onToggled,
}: UseFactDeselectionOptions): UseFactDeselectionReturn {
  // In-session overrides: factId -> desired deselected state. Wins over the
  // server's deselectedAt so an in-flight toggle reflects immediately.
  const [overrides, setOverrides] = useState<Map<string, boolean>>(new Map());

  // Clear overrides when navigating to another message — otherwise an override
  // from message A would leak onto a shared fact id on message B.
  const prevMessageId = useRef(messageId);
  useEffect(() => {
    if (messageId !== prevMessageId.current) {
      prevMessageId.current = messageId;
      setOverrides(new Map());
    }
  }, [messageId]);

  const [toggleMutation] = useMutation<{ messageToggleFactDeselected: boolean }>(
    TOGGLE_FACT_DESELECTED_MUTATION,
    {
      onError: (error) =>
        toast.error(`Auswahl konnte nicht gespeichert werden: ${error.message}`),
    },
  );

  const { dismissedFactIds, suggestedFactIds } = useMemo(() => {
    const dismissed = new Set<string>();
    const suggested = new Set<string>();
    for (const fact of facts ?? []) {
      const override = overrides.get(fact.id);
      if (override !== undefined) {
        if (override) dismissed.add(fact.id);
      } else if (fact.deselectedAt != null) {
        dismissed.add(fact.id);
        if (!fact.deselectedByUser) suggested.add(fact.id);
      }
    }
    return { dismissedFactIds: dismissed, suggestedFactIds: suggested };
  }, [facts, overrides]);

  const toggle = useCallback(
    (factId: string) => {
      if (!messageId) return;
      const next = !dismissedFactIds.has(factId);
      setOverrides((prev) => {
        const m = new Map(prev);
        m.set(factId, next);
        return m;
      });
      void toggleMutation({ variables: { factId, deselected: next } });
      onToggled?.(factId, next);
    },
    [messageId, dismissedFactIds, toggleMutation, onToggled],
  );

  const reset = useCallback(() => setOverrides(new Map()), []);

  return { dismissedFactIds, suggestedFactIds, toggle, reset };
}
